import {
  Crosshair2Icon,
  EnterIcon,
  ExitIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import PartyLogCounter from "src/components/PartryLogCounter";
import { MapDrawer } from "src/components/RouteMap";
import FriendChip from "src/components/active-friends-chips/FriendChip";
import FinishPartyButton from "src/components/parties/finishPartyButton";
import InvitePartyDrawer from "src/components/parties/invitePartyDrawer";
import LeavePartyDialog from "src/components/parties/leavePartyDialog";
import PhotoGallery from "src/components/photo-gallery/photoGallery";
import { Button } from "src/components/ui/button";
import { Separator } from "src/components/ui/separator";
import UsersList from "src/components/users-list/usersList";
import { LogWithUserId } from "src/lib/getLogsFromAlcoholData";
import { useApiRequest } from "src/lib/hooks/useApiRequest";
import { User } from "src/lib/slices/userSlice";
import useSWR from "swr";
import { Party } from "./Parties";

const fetcher = (token) => async (url) => {
  const res = await fetch(url, { headers: { Authorization: token } });

  if (!res.ok) {
    throw await res.json();
  }

  return res.json();
};

export default function PartyPage() {
  const [leaving, setLeaving] = useState(false);
  const [returning, setReturning] = useState(false);
  const partyId = useParams().partyId;

  const apiRequest = useApiRequest();

  const user: User = useSelector((state: any) => state.user);

  const { data, error, isLoading, mutate } = useSWR<Party>(
    `${process.env.REACT_APP_API_URL}/parties/${encodeURIComponent(partyId)}`,
    fetcher(user.token)
  );

  const leave = async () => {
    setLeaving(true);
    const response = await apiRequest.post("/parties/leave", {
      partyId: partyId,
    });
    if (response) {
      if (mutate) mutate();
    } else {
      toast.error("Nepodařilo se opustit kalbu");
    }
    setLeaving(false);
  };

  const returnToParty = async () => {
    setReturning(true);
    const response = await apiRequest.post<
      { error: string } | { success: boolean }
    >("/parties/return", {
      partyId: partyId,
    });
    setReturning(false);
    if (response) {
      if ("error" in response && response.error === "another_party_active") {
        toast.error("Nemůžeš se vrátit do této kalby, dokud máš jinou aktivní");
        setReturning(false);
        return;
      }
      if (mutate) mutate();
      toast.success("Vrátil jsi se do kalby");
    } else {
      toast.error("Nepodařilo se vrátit se do kalby");
    }
  };

  const logs: LogWithUserId[] | undefined = data?.logs;

  const getDate = () => {
    if (!data || "error" in data) return;
    const date = new Date(data?.createdAt) ?? new Date(logs?.[0]?.data.date);
    return date.toLocaleDateString("cs");
  };

  const photos = logs?.map((log) => log.data.photoData).filter(Boolean);

  const isMember = data?.members.includes(user.id);

  return (
    <>
      <div className="px-4 relative">
        {data && (
          <div className="flex flex-col items-end absolute right-4 gap-2">
            <div className="flex items-center gap-2">
              {isMember && (
                <LeavePartyDialog leave={leave}>
                  <Button
                    size="icon"
                    variant="outline"
                    className="gap-2"
                    disabled={leaving}
                  >
                    <ExitIcon />
                  </Button>
                </LeavePartyDialog>
              )}
              {data?.active && <FinishPartyButton mutate={mutate} />}
              {!data?.active && isMember && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={returnToParty}
                  disabled={returning}
                >
                  <EnterIcon />
                  Vrátit se
                </Button>
              )}
            </div>
            {data?.code && (
              <div>
                <InvitePartyDrawer party={data}>
                  <Button className="gap-2">
                    <PlusIcon />
                    Pozvat přátele
                  </Button>
                </InvitePartyDrawer>
              </div>
            )}
          </div>
        )}

        <h2>
          <span
            className="flex items-center gap-2 text-xs text-muted-foreground"
            style={{ lineHeight: "0.75rem" }}
          >
            <span>Kalba</span>
          </span>
          {data && "name" in data && (
            <span className="text-lg font-bold">{data?.name}</span>
          )}
        </h2>

        {error &&
          !isLoading &&
          (error?.error === "not-found" ? (
            <div>Kalba nebyla nalezena</div>
          ) : (
            <div>Nastala chyba v načítání</div>
          ))}

        {isLoading && <Loading className="mt-4" />}

        {data && (
          <>
            <div
              className="text-muted-foreground text-sm"
              style={{ lineHeight: "1rem" }}
            >
              {getDate()}
            </div>
            <div className="flex pt-1 items-center gap-1">
              <UsersList userIds={data.members} showCount={false} />
              <span className="text-muted-foreground">
                {data.members.length}
              </span>
            </div>
            <div className="py-4">
              <Separator />
            </div>
            <PartyLogCounter logs={logs} />
            <MapDrawer asChild logs={logs} showPath={false}>
              <Button variant="outline" className="gap-2 mt-2">
                <Crosshair2Icon />
                Mapa
              </Button>
            </MapDrawer>
            <div className="py-4">
              <Separator />
            </div>
            <div>
              <h3 className="text-base font-bold">Členové</h3>
              {!!data.members.length && (
                <div
                  style={{
                    display: "flex",
                    gap: "1.5rem",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingTop: "1rem",
                    alignItems: "flex-start",
                  }}
                >
                  {data.members.map((userId) => (
                    <FriendChip userId={userId} key={userId} />
                  ))}
                </div>
              )}
              {!data.members.length && (
                <div className="text-muted-foreground">Žádní členové</div>
              )}
            </div>
            <div className="mt-4">
              <h3 className="text-base font-bold">Fotky</h3>
              {!!photos.length && <PhotoGallery photoUrls={photos} />}
              {!photos.length && (
                <div className="text-muted-foreground">Žádné fotky</div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
